import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PriceChange from '@/components/base/PriceChange';
import { useVnGoldPrices } from '@/hooks/useVnGoldPrices';
import { useVnGoldHistory } from '@/hooks/useVnGoldHistory';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';

export default function GoldHistoryPage() {
  const { data: goldList, loading: listLoading } = useVnGoldPrices();
  const [selected, setSelected] = useState('SJL1L10');
  const { data: history, loading: histLoading } = useVnGoldHistory(selected, 30);

  const selectedGold = goldList.find((g) => g.type_code === selected);

  const chartData = history.map((h) => ({
    date: new Date(h.record_date).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' }),
    buy: h.buy,
    sell: h.sell,
  }));

  const latest = history[history.length - 1];
  const previous = history.length > 1 ? history[history.length - 2] : null;
  const dayChange = latest && previous ? latest.buy - previous.buy : 0;
  const dayChangePercent = latest && previous && previous.buy > 0
    ? Number(((dayChange / previous.buy) * 100).toFixed(2))
    : 0;

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16">
        <div className="border-b border-background-200/60 bg-background-100">
          <div className="mx-auto max-w-6xl px-4 md:px-6 py-10">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">Lịch sử giá vàng</h1>
            <p className="mt-2 text-foreground-600">Theo dõi biến động giá vàng theo ngày · 30 ngày gần nhất</p>
          </div>
        </div>

        <div className="mx-auto max-w-6xl px-4 md:px-6 py-8">
          {/* Select gold type */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-foreground-800 mb-2">Chọn loại vàng</label>
            <div className="flex flex-wrap gap-2">
              {listLoading ? (
                <div className="h-9 w-48 bg-background-200 rounded-md animate-pulse"></div>
              ) : (
                goldList.map((g) => (
                  <button
                    key={g.type_code}
                    onClick={() => setSelected(g.type_code)}
                    className={`px-4 py-2 rounded-md text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors border ${
                      selected === g.type_code
                        ? 'bg-primary-500 text-background-950 border-primary-500'
                        : 'bg-background-100 text-foreground-700 border-background-300/60 hover:border-primary-400 hover:text-primary-500'
                    }`}
                  >
                    {g.name}
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="rounded-xl border border-background-200/60 bg-background-100 p-5">
              <p className="text-xs text-foreground-600 mb-1">Giá bán hôm nay</p>
              <p className="text-xl font-bold text-foreground-950 whitespace-nowrap">
                {selectedGold ? selectedGold.sell.toLocaleString('vi-VN') : '—'}đ
              </p>
            </div>
            <div className="rounded-xl border border-background-200/60 bg-background-100 p-5">
              <p className="text-xs text-foreground-600 mb-1">Giá mua hôm nay</p>
              <p className="text-xl font-bold text-foreground-950 whitespace-nowrap">
                {selectedGold ? selectedGold.buy.toLocaleString('vi-VN') : '—'}đ
              </p>
            </div>
            <div className="rounded-xl border border-background-200/60 bg-background-100 p-5">
              <p className="text-xs text-foreground-600 mb-1">So với hôm qua</p>
              {latest && previous ? (
                <PriceChange change={dayChange} changePercent={dayChangePercent} />
              ) : (
                <p className="text-sm text-foreground-500">Chưa có dữ liệu</p>
              )}
            </div>
            <div className="rounded-xl border border-background-200/60 bg-background-100 p-5">
              <p className="text-xs text-foreground-600 mb-1">Số ngày có dữ liệu</p>
              <p className="text-xl font-bold text-foreground-950">{history.length} ngày</p>
            </div>
          </div>

          {/* Chart */}
          <div className="rounded-xl border border-background-200/60 bg-background-100 p-4 md:p-5 mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-foreground-950">
                  Biểu đồ xu hướng · {selectedGold?.name || selected}
                </h3>
                <p className="text-xs text-foreground-600">Đơn vị: VNĐ / lượng · 30 ngày</p>
              </div>
            </div>
            <div className="h-72 md:h-80">
              {histLoading ? (
                <div className="h-full w-full bg-background-200/50 rounded-lg animate-pulse"></div>
              ) : chartData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-foreground-500 text-sm">
                  Chưa có dữ liệu lịch sử cho loại vàng này
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="oklch(var(--background-300) / 0.5)" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: 'oklch(var(--foreground-600))', fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fill: 'oklch(var(--foreground-600))', fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v: number) => `${(v / 1000000).toFixed(1)}tr`}
                      width={52}
                    />
                    <Tooltip
                      contentStyle={{
                        background: 'oklch(var(--background-100))',
                        border: '1px solid oklch(var(--background-300))',
                        borderRadius: 8,
                        color: 'oklch(var(--foreground-950))',
                      }}
                      formatter={(value: number | string, name: string) => [
                        Number(value).toLocaleString('vi-VN') + 'đ',
                        name === 'buy' ? 'Giá mua' : 'Giá bán',
                      ]}
                    />
                    <Legend
                      formatter={(value: string) => (value === 'buy' ? 'Giá mua' : 'Giá bán')}
                      wrapperStyle={{ fontSize: 12, color: 'oklch(var(--foreground-700))' }}
                    />
                    <Line
                      type="monotone"
                      dataKey="buy"
                      stroke="oklch(var(--primary-500))"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="sell"
                      stroke="oklch(var(--accent-500))"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="rounded-xl border border-background-200/60 bg-background-100 overflow-hidden">
            <div className="p-4 md:p-5 border-b border-background-200/60">
              <h3 className="text-sm font-semibold text-foreground-950">Bảng giá theo ngày</h3>
              <p className="text-xs text-foreground-600 mt-1">{selectedGold?.name || selected} · 30 ngày gần nhất</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-background-200/40">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-foreground-700 whitespace-nowrap">Ngày</th>
                    <th className="text-right px-4 py-3 font-semibold text-foreground-700 whitespace-nowrap">Giá mua</th>
                    <th className="text-right px-4 py-3 font-semibold text-foreground-700 whitespace-nowrap">Giá bán</th>
                    <th className="text-right px-4 py-3 font-semibold text-foreground-700 whitespace-nowrap">Chênh lệch</th>
                    <th className="text-right px-4 py-3 font-semibold text-foreground-700 whitespace-nowrap">Thay đổi</th>
                  </tr>
                </thead>
                <tbody>
                  {histLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i} className="border-t border-background-200/40">
                        <td className="px-4 py-3"><div className="h-4 bg-background-200 rounded w-20 animate-pulse"></div></td>
                        <td className="px-4 py-3"><div className="h-4 bg-background-200 rounded w-24 ml-auto animate-pulse"></div></td>
                        <td className="px-4 py-3"><div className="h-4 bg-background-200 rounded w-24 ml-auto animate-pulse"></div></td>
                        <td className="px-4 py-3"><div className="h-4 bg-background-200 rounded w-16 ml-auto animate-pulse"></div></td>
                        <td className="px-4 py-3"><div className="h-4 bg-background-200 rounded w-16 ml-auto animate-pulse"></div></td>
                      </tr>
                    ))
                  ) : history.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-foreground-500 text-sm">
                        Chưa có dữ liệu lịch sử
                      </td>
                    </tr>
                  ) : (
                    [...history].reverse().map((h, idx, arr) => {
                      const prev = arr[idx + 1];
                      const change = prev ? h.buy - prev.buy : 0;
                      const changePercent = prev && prev.buy > 0 ? Number(((change / prev.buy) * 100).toFixed(2)) : 0;
                      const spread = h.sell - h.buy;

                      return (
                        <tr key={h.id} className="border-t border-background-200/40 hover:bg-background-200/30 transition-colors">
                          <td className="px-4 py-3 whitespace-nowrap text-foreground-900 font-medium">
                            {new Date(h.record_date).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap text-foreground-950 font-semibold">
                            {h.buy.toLocaleString('vi-VN')}đ
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap text-foreground-950 font-semibold">
                            {h.sell.toLocaleString('vi-VN')}đ
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap text-foreground-700">
                            +{spread.toLocaleString('vi-VN')}đ
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            {prev ? (
                              <PriceChange change={change} changePercent={changePercent} />
                            ) : (
                              <span className="text-foreground-500 text-xs">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}